/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useRef, useEffect, useState, useCallback } from 'react';
import useMarker from 'src/hooks/useMarker';
import useWatchTime from 'src/hooks/useWatchTime';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { IMarker } from 'src/interfaces/Marker';
import { MARKER_ACTION, NAVIGATION_KEYS } from 'src/interfaces/enums';
import useUI from 'src/hooks/useUI';
import { onAllChangesSaved, onPageHasUnsavedChanges } from 'src/utils/pageLifeCycleUtils';
import Header from '../../components/NotesHeader';
import NotesList from '../../components/NotesList';
import NotesForm from '../../components/NotesForm';
import styles from './styles.module.scss';
import { TIME_TO_WAIT_CONTAINER_FLEX_TRANSITION } from './constants';
import { withNotesContextProvider } from './contexts/NotesContextProvider';
import useNotesContainer from './hooks/useNotesContainer';

export interface INotesContainerProps {
  onHide: () => void;
}

const NotesContainer = ({ onHide }: INotesContainerProps) => {
  const { markers: notes = [] } = useMarker();

  const {
    notesPending,
    handleAddNote,
    handleUpdateNote,
    handleRemoveNote,
    getPendingNoteById,
    handleRetry: handleRetryNotesContainer,
  } = useNotesContainer();

  const { setPlayerTime, currentTime } = useWatchTime();
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const notesHeader = useRef<HTMLDivElement | null>(null);
  const [noteToEdit, setNoteToEdit] = useState<IMarker>({} as IMarker);
  const [isOpen, setIsOpen] = useState(false);
  const [noteFormVisible, setNoteFormVisible] = useState(false);
  const { fullscreen: isFullscreen } = useUI();

  const { lang } = useTranslation();

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    setIsOpen(true);

    if (notes.length === 0) {
      setNoteToEdit({} as IMarker);
      setNoteFormVisible(true);
    }
  }, [notes]);

  useEffect(() => {
    if (notesPending.length > 0) {
      onPageHasUnsavedChanges();
      return;
    }

    onAllChangesSaved();

    return () => {
      onAllChangesSaved();
    };
  }, [notesPending]);

  const handleInterceptionOnHide = useCallback(() => {
    setIsOpen(false);
    setTimeout(onHide, TIME_TO_WAIT_CONTAINER_FLEX_TRANSITION);
  }, [onHide]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const { key } = event;

      if (key !== NAVIGATION_KEYS.ESCAPE) return;

      handleInterceptionOnHide();
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleInterceptionOnHide]);

  const handleOpenNoteForm = useCallback((note: IMarker) => {
    setNoteFormVisible(true);
    setNoteToEdit(note);
  }, []);

  const handleSubmitNote = useCallback(
    (note: IMarker) => {
      setNoteFormVisible(false);
      if (!note?.id) {
        handleAddNote(note);
        return;
      }

      handleUpdateNote(note);
    },
    [handleUpdateNote, handleAddNote],
  );

  const handleDelete = useCallback(
    (note: IMarker) => {
      handleRemoveNote(note);
    },
    [handleRemoveNote],
  );

  const handleRetry = useCallback(
    (note: IMarker) => {
      const pendingNote = getPendingNoteById(note.id);

      if (!pendingNote) {
        return;
      }

      if (pendingNote?.action === MARKER_ACTION.DELETE) {
        handleRetryNotesContainer(pendingNote);
        return;
      }

      handleRetryNotesContainer(pendingNote, () => {
        if (pendingNote?.action === MARKER_ACTION.CREATE) {
          handleOpenNoteForm({ ...note, id: undefined });
        } else handleOpenNoteForm({ ...note });
      });
    },
    [handleRetryNotesContainer, handleOpenNoteForm, getPendingNoteById],
  );

  return (
    <aside
      className={classNames(styles.container, { [styles.open]: isOpen, [styles.fullscreen]: isFullscreen })}
      data-testid='notes-container'
    >
      <Header ref={notesHeader} onHide={handleInterceptionOnHide} />
      <NotesList
        ref={contentScrollRef}
        className={classNames(styles.containerScroll)}
        notes={notes}
        locale={lang}
        onDelete={handleDelete}
        onEdit={handleOpenNoteForm}
        onTimeSelect={setPlayerTime}
        onRetry={handleRetry}
      />
      <NotesForm
        note={noteToEdit}
        visible={noteFormVisible}
        maxTopPosition={notesHeader.current ? notesHeader.current.getBoundingClientRect().bottom : 0}
        onAddNote={() => {
          handleOpenNoteForm({
            time: currentTime,
          } as IMarker);
          setNoteFormVisible(true);
        }}
        onCancel={() => {
          setNoteFormVisible(false);
        }}
        onSubmit={handleSubmitNote}
      />
    </aside>
  );
};

export default withNotesContextProvider(NotesContainer);
